/**
 * 🎨 Container Floating Toolbar - Container Styling System
 * 
 * Floating Toolbar für hero-content-wrapper Container
 * Ermöglicht Inline-Editing von Container-Properties
 * 
 * Features:
 * - Background: Color, Gradient, Image
 * - Padding: Top, Right, Bottom, Left (Desktop/Mobile)
 * - Margin: Top, Right, Bottom, Left (Desktop/Mobile)
 * - Border Radius
 * - Border & Shadow (optional)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ContainerFloatingToolbar.css';

function ContainerFloatingToolbar({
    // Required Props
    isEditMode,
    isVisible,
    
    // Container Style Props
    backgroundColor,
    backgroundColorOpacity = 100, // ✅ NEW: Background Color Opacity (0-100%)
    backdropFilterBlur = 0, // ✅ NEW: Backdrop Filter Blur (0-40px)
    backgroundGradientEnabled,
    backgroundGradientColor1,
    backgroundGradientColor2,
    backgroundGradientDirection = 'to-bottom-right', // ✅ NEW: Gradient Direction
    backgroundImage,
    backgroundImageOpacity,
    
    // ✅ NEW: Mobile Background Props
    backgroundColorMobile,
    backgroundColorOpacityMobile = 100,
    backdropFilterBlurMobile = 0,
    backgroundGradientEnabledMobile,
    backgroundGradientColor1Mobile,
    backgroundGradientColor2Mobile,
    backgroundGradientDirectionMobile, // ✅ NEW: Gradient Direction Mobile
    backgroundImageMobile,
    backgroundImageOpacityMobile,
    backgroundImageSizeMobile,
    backgroundImageScaleMobile,
    backgroundImagePositionMobile,
    
    // Padding (Desktop)
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    
    // Padding (Mobile)
    paddingTopMobile,
    paddingRightMobile,
    paddingBottomMobile,
    paddingLeftMobile,
    
    // Margin (Desktop)
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    
    // Margin (Mobile)
    marginTopMobile,
    marginRightMobile,
    marginBottomMobile,
    marginLeftMobile,
    
    // Border
    borderRadius,
    borderRadiusMobile,
    borderEnabled,
    borderWidth,
    borderColor,
    
    // Shadow
    shadowEnabled,
    shadowIntensity,
    shadowDistance,
    shadowOpacity,
    
    // Background Image Settings
    backgroundImageSize,
    backgroundImageScale,
    backgroundImagePosition,
    
    // Content Alignment
    contentAlignmentVertical,
    contentAlignmentHorizontal,
    
    // Container Dimensions
    containerHeight,
    containerHeightMobile,
    containerWidth,
    containerWidthMobile,
    
    // Callbacks
    onStyleChange, // (styles) => void - komplette Style Updates
    colors = {}, // Brand Colors
    onClose, // () => void - Callback zum Schließen
    
    // Additional Props
    containerRef, // Ref zum Container Element für Position-Tracking
    
    // Responsive
    isMobileLayout = false
}) {
    // 🎨 Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); // 'background', 'padding', 'margin', 'border', 'shadow'
    
    // 🎯 State für Desktop/Mobile Toggle
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 State für Background Desktop/Mobile Toggle
    const [backgroundDevice, setBackgroundDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🖼️ Image Upload State
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    
    // 🎨 CUSTOM COLOR SLOTS - 5 fixe Slots, persistent über LocalStorage (shared with TipTap)
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            console.error('Failed to load custom color slots from localStorage:', error);
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    // 🎨 Aktuell editierter Slot (für Color Picker)
    const [editingSlotIndex, setEditingSlotIndex] = useState(null);
    
    // 🎨 Custom Color Slots: LocalStorage Sync
    useEffect(() => {
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(customColorSlots));
        } catch (error) {
            console.error('Failed to save custom color slots to localStorage:', error);
        }
    }, [customColorSlots]);
    
    // 🎨 Sync Custom Color Slots beim Öffnen der Background Section
    useEffect(() => {
        if (expandedSection === 'background') {
            try {
                const stored = localStorage.getItem('tiptap-custom-color-slots');
                if (stored) {
                    const storedSlots = JSON.parse(stored);
                    if (JSON.stringify(storedSlots) !== JSON.stringify(customColorSlots)) {
                        setCustomColorSlots(storedSlots);
                        console.log('🔄 Custom color slots synchronized from LocalStorage:', storedSlots);
                    }
                }
            } catch (error) {
                console.error('Failed to sync custom color slots from localStorage:', error);
            }
        }
    }, [expandedSection]);
    
    // 🎨 Update Color Slot
    const updateColorSlot = (slotIndex, newColor) => {
        const newSlots = [...customColorSlots];
        newSlots[slotIndex] = newColor;
        setCustomColorSlots(newSlots);
    };
    
    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    // WICHTIG: Keys müssen mit "container" prefix sein für Kompatibilität mit DynamicContainerSection
    const [localStyles, setLocalStyles] = useState({
        containerPaddingTop: paddingTop,
        containerPaddingRight: paddingRight,
        containerPaddingBottom: paddingBottom,
        containerPaddingLeft: paddingLeft,
        containerPaddingTopMobile: paddingTopMobile,
        containerPaddingRightMobile: paddingRightMobile,
        containerPaddingBottomMobile: paddingBottomMobile,
        containerPaddingLeftMobile: paddingLeftMobile,
        containerMarginTop: marginTop,
        containerMarginRight: marginRight,
        containerMarginBottom: marginBottom,
        containerMarginLeft: marginLeft,
        containerMarginTopMobile: marginTopMobile,
        containerMarginRightMobile: marginRightMobile,
        containerMarginBottomMobile: marginBottomMobile,
        containerMarginLeftMobile: marginLeftMobile,
        containerBorderRadius: borderRadius,
        containerBorderRadiusMobile: borderRadiusMobile,
        containerBorderWidth: borderWidth,
        containerShadowIntensity: shadowIntensity,
        containerShadowDistance: shadowDistance,
        containerShadowOpacity: shadowOpacity,
        containerBackgroundColorOpacity: backgroundColorOpacity,
        containerBackgroundColorOpacityMobile: backgroundColorOpacityMobile,
        containerBackdropFilterBlur: backdropFilterBlur,
        containerBackdropFilterBlurMobile: backdropFilterBlurMobile,
        containerBackgroundImageOpacity: backgroundImageOpacity,
        containerBackgroundImageOpacityMobile: backgroundImageOpacityMobile,
        containerBackgroundImageScale: backgroundImageScale,
        containerBackgroundImageScaleMobile: backgroundImageScaleMobile,
        containerHeight: containerHeight,
        containerHeightMobile: containerHeightMobile,
        containerWidth: containerWidth,
        containerWidthMobile: containerWidthMobile
    });

    // ⚠️ REMOVED: Debounced wird nicht mehr benötigt!
    // Parent (HeroHeader) macht das Debouncing jetzt selbst via handleContainerUpdate

    // 🎯 Synchronisiere Device Toggle mit isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
        setBackgroundDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);

    // ✅ Sync showToolbar with isVisible prop
    useEffect(() => {
        // console.log('🔄 ContainerFloatingToolbar visibility changed:', { isEditMode, isVisible });
        
        if (isEditMode && isVisible) {
            setShowToolbar(true);
            updateToolbarPosition();
        } else {
            // console.log('🚫 Hiding toolbar - isEditMode:', isEditMode, 'isVisible:', isVisible);
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isEditMode, isVisible]);

    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        // Verhindere Drag wenn auf Button geklickt
        if (e.target.closest('.container-toolbar-btn')) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    // ✅ Update Toolbar Position - FIXED im Viewport
    const updateToolbarPosition = () => {
        // ✅ FREEZE POSITION: Wenn expandedSection aktiv ist ODER manuell positioniert, Position nicht mehr updaten
        if (expandedSection || isManuallyPositioned) {
            setShowToolbar(true);
            return;
        }
        
        // ✅ EINFACH: Feste Position im Viewport (wie TipTap/Button Toolbar)
        const top = 80; // 80px vom Viewport-Top
        const left = window.innerWidth / 2; // Horizontal zentriert
        
        setToolbarPosition({ top, left });
    };

    // ✅ Update Position on Resize
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResize = () => {
            // Position nur bei Resize aktualisieren (für Zentrierung), nicht wenn manuell positioniert
            if (!expandedSection) {
                updateToolbarPosition();
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [showToolbar, expandedSection, isManuallyPositioned]);

    // ✅ Handle Outside Click - Close toolbar
    useEffect(() => {
        if (!showToolbar || !isVisible) return;
        
        const handleClickOutside = (event) => {
            // Prüfe ob Click in Toolbar war
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            
            // Prüfe ob Click in einem Color Picker war (verhindert falsches Schließen)
            const clickedInColorPicker = event.target.closest('input[type="color"]');
            
            // Prüfe ob Click auf Edit-Button war (dieser togglet die Toolbar, also nicht schließen)
            const clickedOnEditButton = event.target.closest('.container-edit-btn');
            
            console.log('🔍 Click detected:', {
                clickedInToolbar,
                clickedOnEditButton: !!clickedOnEditButton,
                clickedInColorPicker: !!clickedInColorPicker,
                target: event.target
            });
            
            // ✅ Einfache Logik: Schließe Toolbar bei jedem Click AUSSER:
            // 1. Click in der Toolbar selbst
            // 2. Click im Color Picker
            // 3. Click auf dem Edit-Button (der togglet die Toolbar)
            if (!clickedInToolbar && !clickedInColorPicker && !clickedOnEditButton) {
                console.log('🔴 Outside click detected - closing ContainerFloatingToolbar');
                
                // Schließe expanded sections
                setExpandedSection(null);
                
                // Schließe Toolbar
                if (onClose) {
                    console.log('✅ Calling onClose callback');
                    onClose();
                }
            }
        };
        
        // Nutze 'mousedown' statt 'click' für besseres Verhalten
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolbar, isVisible, onClose]);

    // 🚫 Don't render if not in edit mode or not visible
    if (!isEditMode || !isVisible || !showToolbar) {
        return null;
    }

    // ⚡ OPTIMISTIC: Nutze localStyles für sofortiges UI-Feedback
    // 🎨 Active Padding/Margin (responsive) - MIT OPTIMISTIC STATE
    const activePaddingTop = device === 'mobile' 
        ? (localStyles.containerPaddingTopMobile !== undefined && localStyles.containerPaddingTopMobile !== null ? localStyles.containerPaddingTopMobile : (localStyles.containerPaddingTop !== undefined && localStyles.containerPaddingTop !== null ? localStyles.containerPaddingTop : 60))
        : (localStyles.containerPaddingTop !== undefined && localStyles.containerPaddingTop !== null ? localStyles.containerPaddingTop : 80);
    
    const activePaddingRight = device === 'mobile' 
        ? (localStyles.containerPaddingRightMobile !== undefined && localStyles.containerPaddingRightMobile !== null ? localStyles.containerPaddingRightMobile : (localStyles.containerPaddingRight !== undefined && localStyles.containerPaddingRight !== null ? localStyles.containerPaddingRight : 20))
        : (localStyles.containerPaddingRight !== undefined && localStyles.containerPaddingRight !== null ? localStyles.containerPaddingRight : 40);
    
    const activePaddingBottom = device === 'mobile' 
        ? (localStyles.containerPaddingBottomMobile !== undefined && localStyles.containerPaddingBottomMobile !== null ? localStyles.containerPaddingBottomMobile : (localStyles.containerPaddingBottom !== undefined && localStyles.containerPaddingBottom !== null ? localStyles.containerPaddingBottom : 60))
        : (localStyles.containerPaddingBottom !== undefined && localStyles.containerPaddingBottom !== null ? localStyles.containerPaddingBottom : 80);
    
    const activePaddingLeft = device === 'mobile' 
        ? (localStyles.containerPaddingLeftMobile !== undefined && localStyles.containerPaddingLeftMobile !== null ? localStyles.containerPaddingLeftMobile : (localStyles.containerPaddingLeft !== undefined && localStyles.containerPaddingLeft !== null ? localStyles.containerPaddingLeft : 20))
        : (localStyles.containerPaddingLeft !== undefined && localStyles.containerPaddingLeft !== null ? localStyles.containerPaddingLeft : 40);
    
    const activeMarginTop = device === 'mobile' 
        ? (localStyles.containerMarginTopMobile !== undefined && localStyles.containerMarginTopMobile !== null ? localStyles.containerMarginTopMobile : (localStyles.containerMarginTop !== undefined && localStyles.containerMarginTop !== null ? localStyles.containerMarginTop : 0))
        : (localStyles.containerMarginTop !== undefined && localStyles.containerMarginTop !== null ? localStyles.containerMarginTop : 0);
    
    const activeMarginRight = device === 'mobile' 
        ? (localStyles.containerMarginRightMobile !== undefined && localStyles.containerMarginRightMobile !== null ? localStyles.containerMarginRightMobile : (localStyles.containerMarginRight !== undefined && localStyles.containerMarginRight !== null ? localStyles.containerMarginRight : 0))
        : (localStyles.containerMarginRight !== undefined && localStyles.containerMarginRight !== null ? localStyles.containerMarginRight : 0);
    
    const activeMarginBottom = device === 'mobile' 
        ? (localStyles.containerMarginBottomMobile !== undefined && localStyles.containerMarginBottomMobile !== null ? localStyles.containerMarginBottomMobile : (localStyles.containerMarginBottom !== undefined && localStyles.containerMarginBottom !== null ? localStyles.containerMarginBottom : 0))
        : (localStyles.containerMarginBottom !== undefined && localStyles.containerMarginBottom !== null ? localStyles.containerMarginBottom : 0);
    
    const activeMarginLeft = device === 'mobile' 
        ? (localStyles.containerMarginLeftMobile !== undefined && localStyles.containerMarginLeftMobile !== null ? localStyles.containerMarginLeftMobile : (localStyles.containerMarginLeft !== undefined && localStyles.containerMarginLeft !== null ? localStyles.containerMarginLeft : 0))
        : (localStyles.containerMarginLeft !== undefined && localStyles.containerMarginLeft !== null ? localStyles.containerMarginLeft : 0);
    
    const activeBorderRadius = device === 'mobile' 
        ? (localStyles.containerBorderRadiusMobile !== undefined && localStyles.containerBorderRadiusMobile !== null ? localStyles.containerBorderRadiusMobile : (localStyles.containerBorderRadius !== undefined && localStyles.containerBorderRadius !== null ? localStyles.containerBorderRadius : 0))
        : (localStyles.containerBorderRadius !== undefined && localStyles.containerBorderRadius !== null ? localStyles.containerBorderRadius : 0);

    // 🎨 Active Container Dimensions (responsive) - MIT OPTIMISTIC STATE
    const activeContainerHeight = device === 'mobile'
        ? (localStyles.containerHeightMobile !== undefined && localStyles.containerHeightMobile !== null ? localStyles.containerHeightMobile : (localStyles.containerHeight !== undefined && localStyles.containerHeight !== null ? localStyles.containerHeight : 'auto'))
        : (localStyles.containerHeight !== undefined && localStyles.containerHeight !== null ? localStyles.containerHeight : 'auto');
    
    const activeContainerWidth = device === 'mobile'
        ? (localStyles.containerWidthMobile !== undefined && localStyles.containerWidthMobile !== null ? localStyles.containerWidthMobile : (localStyles.containerWidth !== undefined && localStyles.containerWidth !== null ? localStyles.containerWidth : 'auto'))
        : (localStyles.containerWidth !== undefined && localStyles.containerWidth !== null ? localStyles.containerWidth : 'auto');

    // 🎨 Active Opacity/Blur/Scale Values - MIT OPTIMISTIC STATE
    const activeBackgroundColorOpacity = backgroundDevice === 'mobile'
        ? (localStyles.containerBackgroundColorOpacityMobile ?? localStyles.containerBackgroundColorOpacity ?? 100)
        : (localStyles.containerBackgroundColorOpacity ?? 100);

    const activeBackdropFilterBlur = backgroundDevice === 'mobile'
        ? (localStyles.containerBackdropFilterBlurMobile ?? localStyles.containerBackdropFilterBlur ?? 0)
        : (localStyles.containerBackdropFilterBlur ?? 0);

    const activeBackgroundImageOpacity = backgroundDevice === 'mobile'
        ? (localStyles.containerBackgroundImageOpacityMobile ?? localStyles.containerBackgroundImageOpacity ?? 100)
        : (localStyles.containerBackgroundImageOpacity ?? 100);

    const activeBackgroundImageScale = backgroundDevice === 'mobile'
        ? (localStyles.containerBackgroundImageScaleMobile ?? localStyles.containerBackgroundImageScale ?? 100)
        : (localStyles.containerBackgroundImageScale ?? 100);

    const activeBorderWidth = localStyles.containerBorderWidth ?? 1;
    const activeShadowIntensity = localStyles.containerShadowIntensity ?? 15;
    const activeShadowDistance = localStyles.containerShadowDistance ?? 8;
    const activeShadowOpacity = localStyles.containerShadowOpacity ?? 25;

    // ⚡ OPTIMISTIC: Helper Functions - Wie Button/Image Element Pattern
    // 🎨 Helper: Update Padding
    const updatePadding = (side, value) => {
        const parsedValue = parseInt(value);
        const localKey = device === 'mobile' ? `containerPadding${side}Mobile` : `containerPadding${side}`;
        
        // ⚡ SOFORT: Lokalen State updaten (UI reagiert instant!)
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent mit isSliderUpdate = true (für HeroHeader optimistic state)
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true); // isSliderUpdate = true
        }
    };

    // 🎨 Helper: Update Margin
    const updateMargin = (side, value) => {
        const parsedValue = parseInt(value);
        const localKey = device === 'mobile' ? `containerMargin${side}Mobile` : `containerMargin${side}`;
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Border Radius
    const updateBorderRadius = (value) => {
        const parsedValue = parseInt(value);
        const localKey = device === 'mobile' ? 'containerBorderRadiusMobile' : 'containerBorderRadius';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Container Height
    const updateContainerHeight = (value) => {
        const finalValue = value === 'auto' ? 'auto' : parseInt(value);
        const key = device === 'mobile' ? 'containerHeightMobile' : 'containerHeight';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [key]: finalValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [key]: finalValue }, true);
        }
    };

    // 🎨 Helper: Update Container Width
    const updateContainerWidth = (value) => {
        const finalValue = value === 'auto' ? 'auto' : parseInt(value);
        const key = device === 'mobile' ? 'containerWidthMobile' : 'containerWidth';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [key]: finalValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [key]: finalValue }, true);
        }
    };

    // 🎨 Helper: Update Background Color Opacity
    const updateBackgroundColorOpacity = (value) => {
        const parsedValue = parseInt(value);
        const localKey = backgroundDevice === 'mobile' ? 'containerBackgroundColorOpacityMobile' : 'containerBackgroundColorOpacity';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Backdrop Filter Blur
    const updateBackdropFilterBlur = (value) => {
        const parsedValue = parseInt(value);
        const localKey = backgroundDevice === 'mobile' ? 'containerBackdropFilterBlurMobile' : 'containerBackdropFilterBlur';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Background Image Opacity
    const updateBackgroundImageOpacity = (value) => {
        const parsedValue = parseInt(value);
        const localKey = backgroundDevice === 'mobile' ? 'containerBackgroundImageOpacityMobile' : 'containerBackgroundImageOpacity';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Background Image Scale
    const updateBackgroundImageScale = (value) => {
        const parsedValue = parseInt(value);
        const localKey = backgroundDevice === 'mobile' ? 'containerBackgroundImageScaleMobile' : 'containerBackgroundImageScale';
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, [localKey]: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ [localKey]: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Border Width
    const updateBorderWidth = (value) => {
        const parsedValue = parseInt(value);
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, containerBorderWidth: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ containerBorderWidth: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Shadow Intensity
    const updateShadowIntensity = (value) => {
        const parsedValue = parseInt(value);
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, containerShadowIntensity: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ containerShadowIntensity: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Shadow Distance
    const updateShadowDistance = (value) => {
        const parsedValue = parseInt(value);
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, containerShadowDistance: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ containerShadowDistance: parsedValue }, true);
        }
    };

    // 🎨 Helper: Update Shadow Opacity
    const updateShadowOpacity = (value) => {
        const parsedValue = parseInt(value);
        
        // ⚡ SOFORT: Lokalen State updaten
        setLocalStyles(prev => ({ ...prev, containerShadowOpacity: parsedValue }));
        
        // ⚡ SOFORT: Parent Update
        if (onStyleChange) {
            onStyleChange({ containerShadowOpacity: parsedValue }, true);
        }
    };

    // 🖼️ Handle Background Image Upload - FIREBASE STORAGE
    const handleBackgroundImageUpload = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file || !onStyleChange) return;
            
            // File Size Check (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File is too large. Maximum size is 5MB.');
                return;
            }
            
            setIsUploadingImage(true);
            
            try {
                // ✅ FIREBASE STORAGE UPLOAD
                const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
                const { getAuth } = await import('firebase/auth');
                
                const storage = getStorage();
                const auth = getAuth();
                
                if (!auth.currentUser) {
                    alert('You must be logged in to upload images.');
                    setIsUploadingImage(false);
                    return;
                }
                
                const userId = auth.currentUser.uid;
                const timestamp = Date.now();
                const randomId = Math.random().toString(36).substr(2, 9);
                const uniqueFileName = `${timestamp}_${randomId}-${file.name}`;
                
                // Upload to Firebase Storage
                const imageRef = storageRef(storage, `pages/${userId}/container-backgrounds/${uniqueFileName}`);
                const uploadTask = uploadBytesResumable(imageRef, file);

                uploadTask.on('state_changed',
                    (snapshot) => {
                        // Track progress
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`Upload progress: ${progress}%`);
                    },
                    (error) => {
                        console.error('Upload error:', error);
                        alert('Upload failed. Please try again.');
                        setIsUploadingImage(false);
                    },
                    async () => {
                        // Upload complete - get download URL
                        try {
                            const downloadURL = await getDownloadURL(imageRef);
                            
                            // ✅ Update mit Firebase URL (wird in DB gespeichert)
                            // ✅ Speichert auf Desktop oder Mobile basierend auf backgroundDevice
                            if (backgroundDevice === 'mobile') {
                                onStyleChange({ 
                                    containerBackgroundImageMobile: downloadURL,
                                    backgroundImageMobile: downloadURL
                                });
                            } else {
                                onStyleChange({ 
                                    containerBackgroundImage: downloadURL,
                                    backgroundImage: downloadURL
                                });
                            }
                            
                            setIsUploadingImage(false);
                        } catch (error) {
                            console.error('Error getting download URL:', error);
                            alert('Failed to get image URL. Please try again.');
                            setIsUploadingImage(false);
                        }
                    }
                );
            } catch (error) {
                console.error('Error uploading image:', error);
                alert('Upload failed. Please try again.');
                setIsUploadingImage(false);
            }
        };
        input.click();
    };

    // 🗑️ Remove Background Image
    const handleRemoveBackgroundImage = () => {
        if (onStyleChange) {
            onStyleChange({ 
                containerBackgroundImage: null,
                backgroundImage: null
            });
        }
    };

    // 🎨 Toolbar Content (wird über Portal gerendert)
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`container-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="container-toolbar"
                onMouseDown={(e) => {
                    // ✅ FIX: Stoppe alle mousedown Events innerhalb der Toolbar
                    // Verhindert, dass der Outside-Click-Handler triggert
                    e.stopPropagation();
                }}
            >
                {/* 🎯 MAIN TOOLBAR - Immer sichtbar */}
                <div className="container-toolbar-main">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="container-drag-handle"
                        style={{ 
                            cursor: isDragging ? 'grabbing' : 'grab',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '12px',
                            transition: 'all 0.2s ease'
                        }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    {/* Background */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'background' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'background' ? null : 'background')}
                        title="Background"
                    >
                        <i className="fas fa-fill-drip"></i>
                    </button>
                    
                    {/* Padding */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'padding' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'padding' ? null : 'padding')}
                        title="Padding"
                    >
                        <i className="fas fa-expand-arrows-alt"></i>
                    </button>
                    
                    {/* Margin */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'margin' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'margin' ? null : 'margin')}
                        title="Margin"
                    >
                        <i className="fas fa-arrows-alt"></i>
                    </button>
                    
                    {/* Dimensions (Height & Width) */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'dimensions' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'dimensions' ? null : 'dimensions')}
                        title="Height & Width"
                    >
                        <i className="fas fa-ruler-combined"></i>
                    </button>
                    
                    {/* Border */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'border' ? 'active' : ''} ${borderEnabled ? 'has-effect' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'border' ? null : 'border')}
                        title="Border & Radius"
                    >
                        <i className="fas fa-border-style"></i>
                    </button>
                    
                    {/* Shadow */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'shadow' ? 'active' : ''} ${shadowEnabled ? 'has-effect' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'shadow' ? null : 'shadow')}
                        title="Shadow"
                    >
                        <i className="fas fa-cloud"></i>
                    </button>
                    
                    {/* Content Alignment */}
                    <button
                        className={`container-toolbar-btn ${expandedSection === 'alignment' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'alignment' ? null : 'alignment')}
                        title="Content Alignment"
                    >
                        <i className="fas fa-align-center"></i>
                    </button>
                    
                    {/* Divider */}
                    <div className="container-toolbar-divider"></div>
                    
                    {/* Close */}
                    <button
                        className="container-toolbar-btn container-toolbar-btn-close"
                        onClick={() => {
                            setExpandedSection(null);
                            if (onClose) onClose();
                        }}
                        title="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS */}
                
                {/* 1️⃣ BACKGROUND SECTION */}
                {expandedSection === 'background' && (() => {
                    // ✅ Responsive Werte basierend auf backgroundDevice
                    const activeBackgroundColor = backgroundDevice === 'mobile'
                        ? (backgroundColorMobile ?? backgroundColor ?? 'transparent')
                        : (backgroundColor ?? 'transparent');
                    
                    const activeBackgroundColorOpacity = backgroundDevice === 'mobile'
                        ? (backgroundColorOpacityMobile ?? backgroundColorOpacity ?? 100)
                        : (backgroundColorOpacity ?? 100);
                    
                    const activeBackdropFilterBlur = backgroundDevice === 'mobile'
                        ? (backdropFilterBlurMobile ?? backdropFilterBlur ?? 0)
                        : (backdropFilterBlur ?? 0);
                    
                    const activeGradientEnabled = backgroundDevice === 'mobile'
                        ? (backgroundGradientEnabledMobile ?? backgroundGradientEnabled ?? false)
                        : (backgroundGradientEnabled ?? false);
                    
                    const activeGradientColor1 = backgroundDevice === 'mobile'
                        ? (backgroundGradientColor1Mobile ?? backgroundGradientColor1 ?? '#667eea')
                        : (backgroundGradientColor1 ?? '#667eea');
                    
                    const activeGradientColor2 = backgroundDevice === 'mobile'
                        ? (backgroundGradientColor2Mobile ?? backgroundGradientColor2 ?? '#764ba2')
                        : (backgroundGradientColor2 ?? '#764ba2');
                    
                    const activeBackgroundImage = backgroundDevice === 'mobile'
                        ? (backgroundImageMobile ?? backgroundImage ?? '')
                        : (backgroundImage ?? '');
                    
                    const activeBackgroundImageOpacity = backgroundDevice === 'mobile'
                        ? (backgroundImageOpacityMobile ?? backgroundImageOpacity ?? 100)
                        : (backgroundImageOpacity ?? 100);
                    
                    const activeBackgroundImageSize = backgroundDevice === 'mobile'
                        ? (backgroundImageSizeMobile ?? backgroundImageSize ?? 'cover')
                        : (backgroundImageSize ?? 'cover');
                    
                    const activeBackgroundImageScale = backgroundDevice === 'mobile'
                        ? (backgroundImageScaleMobile ?? backgroundImageScale ?? 100)
                        : (backgroundImageScale ?? 100);
                    
                    const activeBackgroundImagePosition = backgroundDevice === 'mobile'
                        ? (backgroundImagePositionMobile ?? backgroundImagePosition ?? 'center')
                        : (backgroundImagePosition ?? 'center');
                    
                    return (
                    <div className="container-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="container-device-toggle">
                            <button
                                className={`container-device-btn ${backgroundDevice === 'desktop' ? 'active' : ''}`}
                                onClick={() => setBackgroundDevice('desktop')}
                            >
                                <i className="fas fa-desktop"></i> Desktop
                            </button>
                            <button
                                className={`container-device-btn ${backgroundDevice === 'mobile' ? 'active' : ''}`}
                                onClick={() => setBackgroundDevice('mobile')}
                            >
                                <i className="fas fa-mobile-alt"></i> Mobile
                            </button>
                        </div>
                        
                        {/* Background Color / Gradient */}
                        <div className="container-color-group">
                            <div className="container-section-header">
                                <span className="container-section-label">Background Color</span>
                                <label className="container-toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={activeGradientEnabled || false}
                                        onChange={(e) => {
                                            if (backgroundDevice === 'mobile') {
                                                onStyleChange({ containerBackgroundGradientEnabledMobile: e.target.checked });
                                            } else {
                                                onStyleChange({ containerBackgroundGradientEnabled: e.target.checked });
                                            }
                                        }}
                                        className="container-toggle-checkbox"
                                    />
                                    <span className="container-toggle-text">Gradient</span>
                                </label>
                            </div>
                            
                            {/* Normal Background Color */}
                            {!activeGradientEnabled && (
                                <>
                                    {/* Transparent Button */}
                                    <button
                                        className="container-transparent-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (backgroundDevice === 'mobile') {
                                                onStyleChange({ containerBackgroundColorMobile: 'transparent' });
                                            } else {
                                                onStyleChange({ containerBackgroundColor: 'transparent' });
                                            }
                                        }}
                                        title="Transparent Background"
                                    >
                                        <div className="container-transparent-preview">
                                            <i className="fas fa-slash"></i>
                                        </div>
                                        <span>Transparent</span>
                                    </button>
                                    
                                    {/* Brand Colors */}
                                    {colors && Object.keys(colors).length > 0 && (
                                        <div className="container-brand-colors">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`container-brand-color ${activeBackgroundColor === brandColor ? 'selected' : ''}`}
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (backgroundDevice === 'mobile') {
                                                            onStyleChange({ containerBackgroundColorMobile: brandColor });
                                                        } else {
                                                            onStyleChange({ containerBackgroundColor: brandColor });
                                                        }
                                                    }}
                                                    title={`Brand Color ${index + 1}`}
                                                >
                                                    {activeBackgroundColor === brandColor && (
                                                        <i className="fas fa-check" style={{ 
                                                            color: 'white', 
                                                            fontSize: '14px',
                                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                        }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* 🎨 CUSTOM COLOR SLOTS - 5 fixe Slots */}
                                    <div className="container-custom-color-slots">
                                        {customColorSlots.map((slotColor, slotIndex) => (
                                            <div key={slotIndex} className="container-color-slot-wrapper">
                                                <button
                                                    type="button"
                                                    className={`container-brand-color container-color-slot ${activeBackgroundColor === slotColor ? 'selected' : ''}`}
                                                    style={{ 
                                                        backgroundColor: slotColor,
                                                        border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        // Wenn Slot leer (weiß), öffne Color Picker
                                                        if (slotColor === '#ffffff') {
                                                            setEditingSlotIndex(slotIndex);
                                                            // Trigger Color Picker
                                                            const colorInput = document.getElementById(`container-color-slot-${slotIndex}`);
                                                            if (colorInput) {
                                                                colorInput.click();
                                                            }
                                                        } else {
                                                            // Ansonsten: Farbe anwenden
                                                            if (backgroundDevice === 'mobile') {
                                                                onStyleChange({ containerBackgroundColorMobile: slotColor });
                                                            } else {
                                                                onStyleChange({ containerBackgroundColor: slotColor });
                                                            }
                                                        }
                                                    }}
                                                    onDoubleClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        // Double-Click: Immer Color Picker öffnen
                                                        setEditingSlotIndex(slotIndex);
                                                        const colorInput = document.getElementById(`container-color-slot-${slotIndex}`);
                                                        if (colorInput) {
                                                            colorInput.click();
                                                        }
                                                    }}
                                                    title={slotColor === '#ffffff' ? `Add Custom Color (Slot ${slotIndex + 1})` : `Custom Color ${slotIndex + 1}: ${slotColor}`}
                                                >
                                                    {/* Plus Icon für leere Slots */}
                                                    {slotColor === '#ffffff' && (
                                                        <i className="fas fa-plus" style={{ 
                                                            color: 'rgba(255, 255, 255, 0.4)', 
                                                            fontSize: '12px'
                                                        }}></i>
                                                    )}
                                                    {/* Checkmark für aktive Farbe */}
                                                    {activeBackgroundColor === slotColor && slotColor !== '#ffffff' && (
                                                        <i className="fas fa-check" style={{ 
                                                            color: 'white', 
                                                            fontSize: '14px',
                                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                        }}></i>
                                                    )}
                                                </button>
                                                
                                                {/* Hidden Color Picker */}
                                                <input
                                                    id={`container-color-slot-${slotIndex}`}
                                                    type="color"
                                                    value={slotColor}
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const newColor = e.target.value;
                                                        updateColorSlot(slotIndex, newColor);
                                                        console.log(`🎨 Slot ${slotIndex} color changed to ${newColor}`);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <span className="container-section-label" style={{ marginTop: '8px', textAlign: 'center', display: 'block' }}>Brand & Custom Colors</span>
                                    
                                    {/* ✅ Background Color Opacity Slider - nur wenn Farbe gesetzt ist */}
                                    {activeBackgroundColor && activeBackgroundColor !== 'transparent' && (
                                        <>
                                            <div className="container-slider-group" style={{ marginTop: '16px' }}>
                                                <label className="container-slider-label">
                                                    <i className="fas fa-adjust"></i> Background Opacity
                                                    <span className="container-slider-value">{activeBackgroundColorOpacity || 100}%</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="5"
                                                    value={activeBackgroundColorOpacity || 100}
                                                    onChange={(e) => updateBackgroundColorOpacity(e.target.value)}
                                                    className="container-slider"
                                                />
                                            </div>
                                            
                                            {/* ✅ Backdrop Filter Blur Slider */}
                                            <div className="container-slider-group" style={{ marginTop: '12px' }}>
                                                <label className="container-slider-label">
                                                    <i className="fas fa-wind"></i> Backdrop Blur
                                                    <span className="container-slider-value">{activeBackdropFilterBlur || 0}px</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="40"
                                                    step="1"
                                                    value={activeBackdropFilterBlur || 0}
                                                    onChange={(e) => updateBackdropFilterBlur(e.target.value)}
                                                    className="container-slider"
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            
                            {/* Gradient Colors */}
                            {activeGradientEnabled && (
                                <div className="container-gradient-controls">
                                    {/* Color 1 */}
                                    <div className="container-gradient-row">
                                        <span className="container-gradient-label">Color 1</span>
                                        <div className="container-gradient-colors-wrapper">
                                            {/* Brand Colors */}
                                            {colors && Object.keys(colors).length > 0 && (
                                                <div className="container-brand-colors">
                                                    {Object.values(colors).map((brandColor, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className={`container-brand-color container-brand-color-small ${activeGradientColor1 === brandColor ? 'selected' : ''}`}
                                                            style={{ backgroundColor: brandColor }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (backgroundDevice === 'mobile') {
                                                                    onStyleChange({ containerBackgroundGradientColor1Mobile: brandColor });
                                                                } else {
                                                                    onStyleChange({ containerBackgroundGradientColor1: brandColor });
                                                                }
                                                            }}
                                                            title={`Gradient Color 1`}
                                                        >
                                                            {activeGradientColor1 === brandColor && (
                                                                <i className="fas fa-check" style={{ 
                                                                    color: 'white', 
                                                                    fontSize: '10px',
                                                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                                }}></i>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Custom Color Slots */}
                                            <div className="container-brand-colors">
                                                {customColorSlots.map((slotColor, slotIndex) => (
                                                    <button
                                                        key={slotIndex}
                                                        type="button"
                                                        className={`container-brand-color container-brand-color-small ${activeGradientColor1 === slotColor ? 'selected' : ''}`}
                                                        style={{ 
                                                            backgroundColor: slotColor,
                                                            border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (slotColor !== '#ffffff') {
                                                                if (backgroundDevice === 'mobile') {
                                                                    onStyleChange({ containerBackgroundGradientColor1Mobile: slotColor });
                                                                } else {
                                                                    onStyleChange({ containerBackgroundGradientColor1: slotColor });
                                                                }
                                                            }
                                                        }}
                                                        title={slotColor === '#ffffff' ? 'Empty Slot' : `Custom Color ${slotIndex + 1}`}
                                                    >
                                                        {activeGradientColor1 === slotColor && slotColor !== '#ffffff' && (
                                                            <i className="fas fa-check" style={{ 
                                                                color: 'white', 
                                                                fontSize: '10px',
                                                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                            }}></i>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Color 2 */}
                                    <div className="container-gradient-row">
                                        <span className="container-gradient-label">Color 2</span>
                                        <div className="container-gradient-colors-wrapper">
                                            {/* Brand Colors */}
                                            {colors && Object.keys(colors).length > 0 && (
                                                <div className="container-brand-colors">
                                                    {Object.values(colors).map((brandColor, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className={`container-brand-color container-brand-color-small ${activeGradientColor2 === brandColor ? 'selected' : ''}`}
                                                            style={{ backgroundColor: brandColor }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (backgroundDevice === 'mobile') {
                                                                    onStyleChange({ containerBackgroundGradientColor2Mobile: brandColor });
                                                                } else {
                                                                    onStyleChange({ containerBackgroundGradientColor2: brandColor });
                                                                }
                                                            }}
                                                            title={`Gradient Color 2`}
                                                        >
                                                            {activeGradientColor2 === brandColor && (
                                                                <i className="fas fa-check" style={{ 
                                                                    color: 'white', 
                                                                    fontSize: '10px',
                                                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                                }}></i>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Custom Color Slots */}
                                            <div className="container-brand-colors">
                                                {customColorSlots.map((slotColor, slotIndex) => (
                                                    <button
                                                        key={slotIndex}
                                                        type="button"
                                                        className={`container-brand-color container-brand-color-small ${activeGradientColor2 === slotColor ? 'selected' : ''}`}
                                                        style={{ 
                                                            backgroundColor: slotColor,
                                                            border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (slotColor !== '#ffffff') {
                                                                if (backgroundDevice === 'mobile') {
                                                                    onStyleChange({ containerBackgroundGradientColor2Mobile: slotColor });
                                                                } else {
                                                                    onStyleChange({ containerBackgroundGradientColor2: slotColor });
                                                                }
                                                            }
                                                        }}
                                                        title={slotColor === '#ffffff' ? 'Empty Slot' : `Custom Color ${slotIndex + 1}`}
                                                    >
                                                        {activeGradientColor2 === slotColor && slotColor !== '#ffffff' && (
                                                            <i className="fas fa-check" style={{ 
                                                                color: 'white', 
                                                                fontSize: '10px',
                                                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                            }}></i>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <span className="container-section-label" style={{ marginTop: '8px', textAlign: 'center', display: 'block' }}>Brand & Custom Colors</span>
                                </div>
                            )}
                            
                            {/* 🎯 Gradient Direction - Nur wenn Gradient aktiviert */}
                            {activeGradientEnabled && (() => {
                                const activeDirection = backgroundDevice === 'mobile' 
                                    ? (backgroundGradientDirectionMobile ?? backgroundGradientDirection ?? 'to-bottom-right')
                                    : (backgroundGradientDirection ?? 'to-bottom-right');
                                
                                return (
                                    <div className="container-gradient-direction-section">
                                        <span className="container-section-label">Gradient Direction</span>
                                        <div className="container-direction-grid">
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-bottom' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-bottom' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-bottom' });
                                                    }
                                                }}
                                                title="Top to Bottom (↓)"
                                            >
                                                <i className="fas fa-arrow-down"></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-right' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-right' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-right' });
                                                    }
                                                }}
                                                title="Left to Right (→)"
                                            >
                                                <i className="fas fa-arrow-right"></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-top' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-top' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-top' });
                                                    }
                                                }}
                                                title="Bottom to Top (↑)"
                                            >
                                                <i className="fas fa-arrow-up"></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-left' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-left' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-left' });
                                                    }
                                                }}
                                                title="Right to Left (←)"
                                            >
                                                <i className="fas fa-arrow-left"></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-bottom-right' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-bottom-right' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-bottom-right' });
                                                    }
                                                }}
                                                title="Diagonal ↘"
                                            >
                                                <i className="fas fa-arrow-right" style={{ transform: 'rotate(45deg)' }}></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-bottom-left' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-bottom-left' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-bottom-left' });
                                                    }
                                                }}
                                                title="Diagonal ↙"
                                            >
                                                <i className="fas fa-arrow-left" style={{ transform: 'rotate(-45deg)' }}></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-top-right' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-top-right' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-top-right' });
                                                    }
                                                }}
                                                title="Diagonal ↗"
                                            >
                                                <i className="fas fa-arrow-right" style={{ transform: 'rotate(-45deg)' }}></i>
                                            </button>
                                            <button
                                                className={`container-direction-btn ${activeDirection === 'to-top-left' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundGradientDirectionMobile: 'to-top-left' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundGradientDirection: 'to-top-left' });
                                                    }
                                                }}
                                                title="Diagonal ↖"
                                            >
                                                <i className="fas fa-arrow-left" style={{ transform: 'rotate(45deg)' }}></i>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                        
                        {/* Background Image Upload */}
                        <div className="container-image-upload-section">
                            <span className="container-section-label">Background Image</span>
                            
                            {/* Image Preview (wenn vorhanden) */}
                            {activeBackgroundImage && (
                                <div className="container-image-preview">
                                    <img 
                                        src={activeBackgroundImage} 
                                        alt="Background" 
                                        style={{
                                            width: '100%',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '6px'
                                        }}
                                    />
                                    <button
                                        className="container-image-remove-btn"
                                        onClick={() => {
                                            if (backgroundDevice === 'mobile') {
                                                onStyleChange({ 
                                                    containerBackgroundImageMobile: null,
                                                    backgroundImageMobile: null
                                                });
                                            } else {
                                                onStyleChange({ 
                                                    containerBackgroundImage: null,
                                                    backgroundImage: null
                                                });
                                            }
                                        }}
                                        title="Remove Background Image"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            )}
                            
                            {/* Upload Button */}
                            <button
                                className="container-image-upload-btn"
                                onClick={handleBackgroundImageUpload}
                                disabled={isUploadingImage}
                            >
                                {isUploadingImage ? (
                                    <>
                                        <div className="container-upload-spinner"></div>
                                        <span>Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-upload"></i>
                                        <span>{activeBackgroundImage ? 'Change Image' : 'Upload Image'}</span>
                                    </>
                                )}
                            </button>
                            
                            {/* Image Settings (wenn Image vorhanden) */}
                            {activeBackgroundImage && (
                                <>
                                    {/* Image Size */}
                                    <div className="container-image-size-group" style={{ marginTop: '12px' }}>
                                        <span className="container-section-label" style={{ marginBottom: '8px', display: 'block' }}>Image Size</span>
                                        <div className="container-size-buttons">
                                            <button
                                                className={`container-size-btn ${(activeBackgroundImageSize || 'cover') === 'cover' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundImageSizeMobile: 'cover' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundImageSize: 'cover' });
                                                    }
                                                }}
                                            >
                                                Cover
                                            </button>
                                            <button
                                                className={`container-size-btn ${activeBackgroundImageSize === 'contain' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundImageSizeMobile: 'contain' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundImageSize: 'contain' });
                                                    }
                                                }}
                                            >
                                                Contain
                                            </button>
                                            <button
                                                className={`container-size-btn ${activeBackgroundImageSize === 'auto' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (backgroundDevice === 'mobile') {
                                                        onStyleChange({ containerBackgroundImageSizeMobile: 'auto' });
                                                    } else {
                                                        onStyleChange({ containerBackgroundImageSize: 'auto' });
                                                    }
                                                }}
                                            >
                                                Auto
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Image Opacity Slider */}
                                    <div className="container-slider-group" style={{ marginTop: '12px' }}>
                                        <label className="container-slider-label">
                                            Image Opacity
                                            <span className="container-slider-value">{activeBackgroundImageOpacity || 100}%</span>
                                        </label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={activeBackgroundImageOpacity || 100}
                                            onChange={(e) => updateBackgroundImageOpacity(e.target.value)}
                                            className="container-slider"
                                        />
                                    </div>
                                    
                                    {/* Image Scale Slider */}
                                    <div className="container-slider-group" style={{ marginTop: '12px' }}>
                                        <label className="container-slider-label">
                                            Image Scale
                                            <span className="container-slider-value">{activeBackgroundImageScale || 100}%</span>
                                        </label>
                                        <input
                                            type="range"
                                            min="100"
                                            max="400"
                                            step="5"
                                            value={activeBackgroundImageScale || 100}
                                            onChange={(e) => updateBackgroundImageScale(e.target.value)}
                                            className="container-slider"
                                        />
                                    </div>
                                    
                                    {/* Image Position Grid */}
                                    <div style={{ marginTop: '16px' }}>
                                        <span className="container-section-label" style={{ marginBottom: '8px', display: 'block' }}>Image Position</span>
                                        <div className="container-image-position-grid">
                                            {['top left', 'top center', 'top right', 'center left', 'center', 'center right', 'bottom left', 'bottom center', 'bottom right'].map((position) => {
                                                const isActive = (activeBackgroundImagePosition || 'center') === position;
                                                const icon = position === 'top left' ? '↖' : 
                                                            position === 'top center' ? '↑' : 
                                                            position === 'top right' ? '↗' : 
                                                            position === 'center left' ? '←' : 
                                                            position === 'center' ? '⊙' : 
                                                            position === 'center right' ? '→' : 
                                                            position === 'bottom left' ? '↙' : 
                                                            position === 'bottom center' ? '↓' : '↘';
                                                
                                                return (
                                                    <button
                                                        key={position}
                                                        className={`container-position-btn ${isActive ? 'active' : ''}`}
                                                        onClick={() => {
                                                            if (backgroundDevice === 'mobile') {
                                                                onStyleChange({ containerBackgroundImagePositionMobile: position });
                                                            } else {
                                                                onStyleChange({ containerBackgroundImagePosition: position });
                                                            }
                                                        }}
                                                        title={position}
                                                    >
                                                        {icon}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    );
                })()}
                
                {/* 7️⃣ CONTENT ALIGNMENT SECTION - Eigene Section für bessere Sichtbarkeit */}
                {expandedSection === 'alignment' && (
                    <div className="container-expanded-section">
                        {/* Section Header */}
                        <div className="container-section-title-header">
                            <i className="fas fa-align-center"></i>
                            <span>CONTENT ALIGNMENT</span>
                            <span className="container-section-description">Align items inside the container</span>
                        </div>
                        
                        {/* Horizontal Alignment */}
                        <div className="container-alignment-group">
                            <span className="container-alignment-label">Horizontal</span>
                            <div className="container-alignment-buttons">
                                <button
                                    className={`container-alignment-btn ${(contentAlignmentHorizontal || 'flex-start') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentHorizontal: 'flex-start' })}
                                    title="Left"
                                >
                                    <i className="fas fa-arrow-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    className={`container-alignment-btn ${contentAlignmentHorizontal === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentHorizontal: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`container-alignment-btn ${contentAlignmentHorizontal === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentHorizontal: 'flex-end' })}
                                    title="Right"
                                >
                                    <i className="fas fa-arrow-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Vertical Alignment */}
                        <div className="container-alignment-group">
                            <span className="container-alignment-label">Vertical</span>
                            <div className="container-alignment-buttons">
                                <button
                                    className={`container-alignment-btn ${(contentAlignmentVertical || 'center') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentVertical: 'flex-start' })}
                                    title="Top"
                                >
                                    <i className="fas fa-arrow-up"></i>
                                    <span>Top</span>
                                </button>
                                <button
                                    className={`container-alignment-btn ${(contentAlignmentVertical || 'center') === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentVertical: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`container-alignment-btn ${(contentAlignmentVertical || 'center') === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ containerContentAlignmentVertical: 'flex-end' })}
                                    title="Bottom"
                                >
                                    <i className="fas fa-arrow-down"></i>
                                    <span>Bottom</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* 2️⃣ PADDING SECTION */}
                {expandedSection === 'padding' && (
                    <div className="container-expanded-section">
                        {/* Section Header */}
                        <div className="container-section-title-header">
                            <i className="fas fa-expand-arrows-alt"></i>
                            <span>PADDING</span>
                            <span className="container-section-description">Internal spacing inside the container</span>
                        </div>
                        
                        {/* Desktop/Mobile Toggle */}
                        <div className="container-device-toggle">
                            <button
                                className={`container-device-btn ${device === 'desktop' ? 'active' : ''}`}
                                onClick={() => setDevice('desktop')}
                            >
                                <i className="fas fa-desktop"></i> Desktop
                            </button>
                            <button
                                className={`container-device-btn ${device === 'mobile' ? 'active' : ''}`}
                                onClick={() => setDevice('mobile')}
                            >
                                <i className="fas fa-mobile-alt"></i> Mobile
                            </button>
                        </div>
                        
                        {/* Padding Controls */}
                        <div className="container-spacing-controls">
                            {/* Top */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-up"></i> Top
                                    <span className="container-slider-value">{activePaddingTop}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activePaddingTop}
                                    onChange={(e) => updatePadding('Top', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Right */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-right"></i> Right
                                    <span className="container-slider-value">{activePaddingRight}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activePaddingRight}
                                    onChange={(e) => updatePadding('Right', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Bottom */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-down"></i> Bottom
                                    <span className="container-slider-value">{activePaddingBottom}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activePaddingBottom}
                                    onChange={(e) => updatePadding('Bottom', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Left */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-left"></i> Left
                                    <span className="container-slider-value">{activePaddingLeft}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activePaddingLeft}
                                    onChange={(e) => updatePadding('Left', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* 3️⃣ MARGIN SECTION */}
                {expandedSection === 'margin' && (
                    <div className="container-expanded-section">
                        {/* Section Header */}
                        <div className="container-section-title-header">
                            <i className="fas fa-arrows-alt"></i>
                            <span>MARGIN</span>
                            <span className="container-section-description">External spacing around the container</span>
                        </div>
                        
                        {/* Desktop/Mobile Toggle */}
                        <div className="container-device-toggle">
                            <button
                                className={`container-device-btn ${device === 'desktop' ? 'active' : ''}`}
                                onClick={() => setDevice('desktop')}
                            >
                                <i className="fas fa-desktop"></i> Desktop
                            </button>
                            <button
                                className={`container-device-btn ${device === 'mobile' ? 'active' : ''}`}
                                onClick={() => setDevice('mobile')}
                            >
                                <i className="fas fa-mobile-alt"></i> Mobile
                            </button>
                        </div>
                        
                        {/* Margin Controls */}
                        <div className="container-spacing-controls">
                            {/* Top */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-up"></i> Top
                                    <span className="container-slider-value">{activeMarginTop}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activeMarginTop}
                                    onChange={(e) => updateMargin('Top', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Right */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-right"></i> Right
                                    <span className="container-slider-value">{activeMarginRight}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activeMarginRight}
                                    onChange={(e) => updateMargin('Right', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Bottom */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-down"></i> Bottom
                                    <span className="container-slider-value">{activeMarginBottom}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activeMarginBottom}
                                    onChange={(e) => updateMargin('Bottom', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                            
                            {/* Left */}
                            <div className="container-slider-group">
                                <label className="container-slider-label">
                                    <i className="fas fa-arrow-left"></i> Left
                                    <span className="container-slider-value">{activeMarginLeft}px</span>
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={activeMarginLeft}
                                    onChange={(e) => updateMargin('Left', e.target.value)}
                                    className="container-slider"
                                />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* 4️⃣ DIMENSIONS SECTION (Height & Width) */}
                {expandedSection === 'dimensions' && (
                    <div className="container-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="container-device-toggle">
                            <button
                                className={`container-device-btn ${device === 'desktop' ? 'active' : ''}`}
                                onClick={() => setDevice('desktop')}
                            >
                                <i className="fas fa-desktop"></i> Desktop
                            </button>
                            <button
                                className={`container-device-btn ${device === 'mobile' ? 'active' : ''}`}
                                onClick={() => setDevice('mobile')}
                            >
                                <i className="fas fa-mobile-alt"></i> Mobile
                            </button>
                        </div>
                        
                        {/* Height Control */}
                        <div className="container-dimension-group">
                            <div className="container-dimension-header">
                                <label className="container-slider-label" style={{ marginBottom: 0 }}>
                                    <i className="fas fa-arrows-alt-v"></i> Height
                                    <span className="container-slider-value">{activeContainerHeight === 'auto' ? 'Auto' : `${activeContainerHeight}px`}</span>
                                </label>
                                <button
                                    className={`container-auto-btn ${activeContainerHeight === 'auto' ? 'active' : ''}`}
                                    onClick={() => updateContainerHeight('auto')}
                                >
                                    Auto
                                </button>
                            </div>
                            {activeContainerHeight !== 'auto' && (
                                <input
                                    type="range"
                                    min="200"
                                    max="1200"
                                    step="10"
                                    value={activeContainerHeight}
                                    onChange={(e) => updateContainerHeight(e.target.value)}
                                    className="container-slider"
                                />
                            )}
                        </div>
                        
                        {/* Width Control */}
                        <div className="container-dimension-group">
                            <div className="container-dimension-header">
                                <label className="container-slider-label" style={{ marginBottom: 0 }}>
                                    <i className="fas fa-arrows-alt-h"></i> Width
                                    <span className="container-slider-value">{activeContainerWidth === 'auto' ? '100%' : `${activeContainerWidth}px`}</span>
                                </label>
                                <button
                                    className={`container-auto-btn ${activeContainerWidth === 'auto' ? 'active' : ''}`}
                                    onClick={() => updateContainerWidth('auto')}
                                >
                                    100%
                                </button>
                            </div>
                            {activeContainerWidth !== 'auto' && (
                                <input
                                    type="range"
                                    min="200"
                                    max="2000"
                                    step="10"
                                    value={activeContainerWidth}
                                    onChange={(e) => updateContainerWidth(e.target.value)}
                                    className="container-slider"
                                />
                            )}
                        </div>
                    </div>
                )}
                
                {/* 5️⃣ BORDER SECTION */}
                {expandedSection === 'border' && (
                    <div className="container-expanded-section">
                        {/* Border Radius */}
                        <div className="container-slider-group">
                            <label className="container-slider-label">
                                Border Radius ({device === 'mobile' ? '📱' : '💻'})
                                <span className="container-slider-value">{activeBorderRadius}px</span>
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={activeBorderRadius}
                                onChange={(e) => updateBorderRadius(e.target.value)}
                                className="container-slider"
                            />
                        </div>
                        
                        {/* Border Enable */}
                        <div className="container-toggle-group">
                            <label className="container-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={borderEnabled || false}
                                    onChange={(e) => onStyleChange({ containerBorderEnabled: e.target.checked })}
                                    className="container-toggle-checkbox"
                                />
                                <span className="container-toggle-text">Enable Border</span>
                            </label>
                        </div>
                        
                        {/* Border Width (wenn enabled) */}
                        {borderEnabled && (
                            <>
                                <div className="container-slider-group">
                                    <label className="container-slider-label">
                                        Border Width
                                        <span className="container-slider-value">{borderWidth || 1}px</span>
                                    </label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={activeBorderWidth}
                                        onChange={(e) => updateBorderWidth(e.target.value)}
                                        className="container-slider"
                                    />
                                </div>
                                
                                {/* Border Color */}
                                <div className="container-color-group">
                                    <span className="container-color-label">Border Color</span>
                                    
                                    {/* Brand Colors */}
                                    {colors && Object.keys(colors).length > 0 && (
                                        <div className="container-brand-colors">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`container-brand-color container-brand-color-small ${borderColor === brandColor ? 'selected' : ''}`}
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ containerBorderColor: brandColor });
                                                    }}
                                                    title={`Border Color ${index + 1}`}
                                                >
                                                    {borderColor === brandColor && (
                                                        <i className="fas fa-check" style={{ 
                                                            color: 'white', 
                                                            fontSize: '10px',
                                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                        }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Custom Color Slots */}
                                    <div className="container-brand-colors">
                                        {customColorSlots.map((slotColor, slotIndex) => (
                                            <button
                                                key={slotIndex}
                                                type="button"
                                                className={`container-brand-color container-brand-color-small ${borderColor === slotColor ? 'selected' : ''}`}
                                                style={{ 
                                                    backgroundColor: slotColor,
                                                    border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (slotColor !== '#ffffff') {
                                                        onStyleChange({ containerBorderColor: slotColor });
                                                    }
                                                }}
                                                title={slotColor === '#ffffff' ? 'Empty Slot' : `Custom Color ${slotIndex + 1}`}
                                            >
                                                {borderColor === slotColor && slotColor !== '#ffffff' && (
                                                    <i className="fas fa-check" style={{ 
                                                        color: 'white', 
                                                        fontSize: '10px',
                                                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                    }}></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                
                {/* 6️⃣ SHADOW SECTION */}
                {expandedSection === 'shadow' && (
                    <div className="container-expanded-section">
                        {/* Shadow Enable */}
                        <div className="container-toggle-group">
                            <label className="container-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={shadowEnabled || false}
                                    onChange={(e) => onStyleChange({ containerShadowEnabled: e.target.checked })}
                                    className="container-toggle-checkbox"
                                />
                                <span className="container-toggle-text">Enable Shadow</span>
                            </label>
                        </div>
                        
                        {/* Shadow Controls (wenn enabled) */}
                        {shadowEnabled && (
                            <>
                                {/* Blur */}
                                <div className="container-slider-group">
                                    <label className="container-slider-label">
                                        Blur
                                        <span className="container-slider-value">{shadowIntensity || 15}px</span>
                                    </label>
                                    <input
                                        type="range"
                                        min="5"
                                        max="50"
                                        value={activeShadowIntensity}
                                        onChange={(e) => updateShadowIntensity(e.target.value)}
                                        className="container-slider"
                                    />
                                </div>
                                
                                {/* Distance */}
                                <div className="container-slider-group">
                                    <label className="container-slider-label">
                                        Distance
                                        <span className="container-slider-value">{shadowDistance || 8}px</span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="30"
                                        value={activeShadowDistance}
                                        onChange={(e) => updateShadowDistance(e.target.value)}
                                        className="container-slider"
                                    />
                                </div>
                                
                                {/* Opacity */}
                                <div className="container-slider-group">
                                    <label className="container-slider-label">
                                        Opacity
                                        <span className="container-slider-value">{shadowOpacity || 25}%</span>
                                    </label>
                                    <input
                                        type="range"
                                        min="5"
                                        max="80"
                                        value={activeShadowOpacity}
                                        onChange={(e) => updateShadowOpacity(e.target.value)}
                                        className="container-slider"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default ContainerFloatingToolbar;

